import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import SubAccountLayout from '../../components/SubAccountLayout';
import { useSubAccount } from '../../components/SubAccountLayout';
import { useTheme } from '../../context/ThemeContext';
import { toast } from 'sonner';
import { SmsActivity, smsEvents } from '../../components/agent/SmsActivity';
import {
  History, Search, PhoneIncoming, PhoneOutgoing, Play, Pause,
  FileText, Clock, CheckCircle, XCircle, ChevronRight, X,
  Download, Calendar, Bot, User, Loader2, Volume2,
  RefreshCw, Copy, ArrowUpRight, ArrowDownLeft,
  Wrench, Tag, MessageSquare
} from 'lucide-react';

// Audio Player Component
const AudioPlayer = ({ url, callId }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const { subAccountId } = useParams();

  const API_URL = window.location.origin;
  const proxyUrl = `${API_URL}/api/sub-accounts/${subAccountId}/calls/${callId}/recording/stream`;

  const toggle = (e) => {
    e?.stopPropagation();
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); }
    else {
      if (!audioRef.current.src || audioRef.current.src === '') {
        const token = localStorage.getItem('token');
        fetch(proxyUrl, { headers: { Authorization: `Bearer ${token}` } })
          .then(r => { if (!r.ok) throw new Error('Failed'); return r.blob(); })
          .then(blob => {
            audioRef.current.src = URL.createObjectURL(blob);
            audioRef.current.play().catch(() => {});
          })
          .catch(() => toast.error('Failed to load recording'));
        setPlaying(true);
        return;
      }
      audioRef.current.play().catch(() => {});
    }
    setPlaying(!playing);
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    const pct = (audioRef.current.currentTime / audioRef.current.duration) * 100;
    setProgress(pct || 0);
  };

  const handleSeek = (e) => {
    e?.stopPropagation();
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = pct * duration;
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const handleDownload = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(proxyUrl, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await resp.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `recording-${callId}.mp3`;
      a.click();
      URL.revokeObjectURL(downloadUrl);
    } catch {
      // Silently fail
    }
  };

  return (
    <div className="flex items-center gap-3 w-full" onClick={e => e.stopPropagation()} data-testid={`audio-player-${callId}`}>
      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
        onEnded={() => setPlaying(false)}
        preload="none"
      />
      <button
        onClick={toggle}
        className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
          playing ? 'bg-teal-400 text-[#08090d]' : 'bg-white/10 text-white hover:bg-white/20'
        }`}
        data-testid={`audio-play-${callId}`}
      >
        {playing ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="h-1.5 bg-white/10 rounded-full cursor-pointer overflow-hidden" onClick={handleSeek}>
          <div className="h-full bg-teal-400 rounded-full transition-all" style={{ width: `${progress}%` }} />
        </div>
      </div>
      <span className="text-gray-500 text-xs font-mono flex-shrink-0">
        {fmt(audioRef.current?.currentTime || 0)} / {fmt(duration)}
      </span>
      <button onClick={handleDownload} className="p-1.5 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors" data-testid={`audio-download-${callId}`}>
        <Download size={14} />
      </button>
    </div>
  );
};

// Outcome Badge Component
const FAILURE_REASON_LABELS = {
  bridge_lookup_miss: 'Bridge could not find the call record',
  warm_session_missing: 'Pre-warmed AI session was missing when the bridge connected',
  el_attach_failed: 'AI voice bridge failed to attach — usually a stale ElevenLabs WebSocket',
  ws_timeout: 'WebSocket to ElevenLabs timed out',
  twiml_bridge_webhook: 'Twilio bridge webhook fallback fired',
};

const TAG_PILL_STYLES = {
  'answered':         'bg-emerald-500/15 text-emerald-300 border-emerald-400/30',
  'not answered':    'bg-gray-500/15 text-gray-300 border-gray-400/25',
  'ai voice booked':  'bg-teal-500/15 text-teal-300 border-teal-400/30',
  'ai chat booked':   'bg-violet-500/15 text-violet-300 border-violet-400/30',
};

const GhlTagPills = ({ tags }) => {
  if (!tags || !Array.isArray(tags) || tags.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1" data-testid="ghl-tag-pills">
      {tags.map((t) => (
        <span
          key={t}
          className={`text-[10px] px-1.5 py-0.5 rounded-md font-medium border ${TAG_PILL_STYLES[t] || 'bg-slate-500/15 text-slate-300 border-slate-400/25'}`}
          title={`LeadConnector tag applied: ${t}`}
          data-testid={`ghl-tag-pill-${t.replace(/\s+/g, '-')}`}
        >
          {t}
        </span>
      ))}
    </div>
  );
};

const CallStatusBadge = ({ call }) => {
  // Explicit failure recorded by our bridge/WS handlers — this trumps
  // whatever Twilio later reports (e.g. "completed" for a 3-second call
  // that only played our fallback TwiML).
  if (call.failure_reason) {
    const reason = FAILURE_REASON_LABELS[call.failure_reason] || call.failure_reason;
    const detail = call.failure_detail ? `\nDetail: ${call.failure_detail}` : '';
    const tooltip =
      `Reason: ${reason}\nStage: ${call.failure_stage || 'unknown'}${detail}\nCall ID: ${call.call_id || '—'}`;
    return (
      <span
        className="text-xs px-2 py-0.5 rounded font-medium bg-red-500/15 text-red-300 border border-red-400/30 cursor-help"
        title={tooltip}
        data-testid="call-status-badge"
      >
        failed
      </span>
    );
  }
  return (
    <span className={`text-xs px-2 py-0.5 rounded font-medium ${
      call.status === 'completed' ? 'bg-green-400/10 text-green-400'
      : call.status === 'missed' ? 'bg-red-400/10 text-red-400'
      : call.status === 'in-progress' ? 'bg-blue-400/10 text-blue-400'
      : call.status === 'failed' ? 'bg-red-500/15 text-red-300 border border-red-400/30'
      : 'bg-gray-400/10 text-gray-400'
    }`} data-testid="call-status-badge" title={`Call ID: ${call.call_id || '—'}`}>
      {call.status}
    </span>
  );
};

const OutcomeBadge = ({ outcome }) => {
  const config = {
    appointment_booked: { label: 'Booked', color: 'bg-green-400/10 text-green-400 border-green-400/20' },
    callback_scheduled: { label: 'Callback', color: 'bg-blue-400/10 text-blue-400 border-blue-400/20' },
    lead_qualified: { label: 'Qualified', color: 'bg-violet-400/10 text-violet-400 border-violet-400/20' },
    issue_resolved: { label: 'Resolved', color: 'bg-emerald-400/10 text-emerald-400 border-emerald-400/20' },
    transferred: { label: 'Transferred', color: 'bg-amber-400/10 text-amber-400 border-amber-400/20' },
    no_action: { label: 'No Action', color: 'bg-gray-400/10 text-gray-400 border-gray-400/20' },
    voicemail_left: { label: 'Voicemail', color: 'bg-orange-400/10 text-orange-400 border-orange-400/20' },
    voicemail_detected: { label: 'Voicemail', color: 'bg-orange-400/10 text-orange-400 border-orange-400/20' },
    interested: { label: 'Interested', color: 'bg-teal-400/10 text-teal-400 border-teal-400/20' },
    not_interested: { label: 'Not Interested', color: 'bg-red-400/10 text-red-400 border-red-400/20' },
    demo_scheduled: { label: 'Demo', color: 'bg-cyan-400/10 text-cyan-400 border-cyan-400/20' },
    concurrency_limit: { label: 'Concurrent limit reached', color: 'bg-rose-400/10 text-rose-300 border-rose-400/30' },
  };
  const c = config[outcome] || config.no_action;
  return (
    <span className={`px-2 py-0.5 rounded-md text-[11px] font-medium border ${c.color}`} data-testid="outcome-badge">
      {c.label}
    </span>
  );
};

// Call Detail Slide-Over Panel
const CallDetailPanel = ({ call, onClose, textPrimary, textSecondary, inputBg, cardBorder }) => {
  if (!call) return null;

  const formatDuration = (s) => {
    if (!s) return '0:00';
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-end"
      onClick={onClose}
    >
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        className="w-full max-w-2xl bg-[#0c0d12] border-l border-white/[0.08] h-full overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="call-detail-panel"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#0c0d12] border-b border-white/[0.08] p-5">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                call.direction === 'inbound' ? 'bg-teal-400/10' : 'bg-violet-400/10'
              }`}>
                {call.direction === 'inbound'
                  ? <PhoneIncoming className="text-teal-400" size={22} />
                  : <PhoneOutgoing className="text-violet-400" size={22} />}
              </div>
              <div>
                <h3 className={`text-lg font-semibold ${textPrimary}`}>{call.contact_name || call.phone_number}</h3>
                <div className="flex items-center gap-3 mt-0.5">
                  <span className={`text-sm ${textSecondary}`}>{call.phone_number}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    call.direction === 'inbound' ? 'bg-teal-400/10 text-teal-400' : 'bg-violet-400/10 text-violet-400'
                  }`}>
                    {call.direction === 'inbound' ? 'Inbound' : 'Outbound'}
                  </span>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:bg-white/10">
              <X size={18} />
            </button>
          </div>

          {/* Stats Row */}
          <div className="flex items-center gap-5 mt-4 flex-wrap">
            <div className="flex items-center gap-1.5">
              <Clock size={13} className="text-gray-500" />
              <span className="text-gray-400 text-sm">{formatDuration(call.duration)}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Bot size={13} className="text-teal-400" />
              <span className="text-gray-400 text-sm">{call.agent_name || 'AI Agent'}</span>
            </div>
            <CallStatusBadge call={call} />
            {call.outcome && <OutcomeBadge outcome={call.outcome} />}
            <GhlTagPills tags={call.tags_applied} />
          </div>
        </div>

        <div className="p-5 space-y-6">
          {/* Summary */}
          {call.summary && (
            <div data-testid="call-summary">
              <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Summary</h4>
              <div className={`p-4 rounded-xl ${inputBg} border ${cardBorder}`}>
                <p className="text-sm text-gray-300">{call.summary}</p>
              </div>
            </div>
          )}

          {/* Recording */}
          {call.has_recording && call.recording_url && (
            <div data-testid="call-recording">
              <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Recording</h4>
              <div className={`p-4 rounded-xl ${inputBg} border ${cardBorder}`}>
                <AudioPlayer url={call.recording_url} callId={call.call_id} />
              </div>
            </div>
          )}

          {/* Transcript */}
          <div data-testid="call-transcript">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Transcript</h4>
            {call.transcript?.length > 0 ? (
              <div className="space-y-3">
                {call.transcript.map((msg, idx) => (
                  <div key={idx} className={`flex gap-2.5 ${msg.role === 'agent' ? '' : 'flex-row-reverse'}`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                      msg.role === 'agent' ? 'bg-teal-400/10' : 'bg-violet-400/10'
                    }`}>
                      {msg.role === 'agent' ? <Bot size={13} className="text-teal-400" /> : <User size={13} className="text-violet-400" />}
                    </div>
                    <div className={`flex-1 ${msg.role === 'agent' ? '' : 'text-right'}`}>
                      <div className={`inline-block max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm ${
                        msg.role === 'agent'
                          ? 'bg-white/5 text-gray-200'
                          : 'bg-violet-400/10 text-violet-100'
                      }`}>
                        {msg.text}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={`p-8 rounded-xl ${inputBg} border ${cardBorder} text-center`}>
                <FileText size={28} className="mx-auto text-gray-600 mb-2" />
                <p className="text-gray-500 text-sm">No transcript available</p>
              </div>
            )}
          </div>

          {/* Function Calls */}
          <SmsActivity entry={call} testId={`inbox-sms-${call.call_id}`} />
          {call.function_calls?.some(fc => fc.name !== 'send_sms' || !smsEvents(call).length) && (
            <div data-testid="call-functions">
              <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Function Calls</h4>
              <div className="space-y-2">
                {call.function_calls.filter(fc => fc.name !== 'send_sms' || !smsEvents(call).length).map((fc, idx) => (
                  <div key={idx} className={`p-3 rounded-xl ${inputBg} border ${cardBorder}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Wrench size={12} className="text-teal-400" />
                        <span className="text-sm font-mono text-teal-400">{fc.name}</span>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded ${
                        fc.result === 'success' ? 'bg-green-400/10 text-green-400' : 'bg-red-400/10 text-red-400'
                      }`}>{fc.result}</span>
                    </div>
                    {fc.params && (
                      <pre className="text-[11px] text-gray-500 font-mono mt-1.5 overflow-x-auto">
                        {JSON.stringify(fc.params, null, 2)}
                      </pre>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <button
              onClick={() => {
                const text = call.transcript?.map(m => `${m.role}: ${m.text}`).join('\n') || '';
                navigator.clipboard.writeText(text);
                toast.success('Transcript copied');
              }}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-white/5 border border-white/[0.08] text-white text-sm hover:bg-white/10 transition-colors"
              data-testid="copy-transcript-btn"
            >
              <Copy size={15} /> Copy Transcript
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Main Content
const CallHistoryContent = () => {
  const { subAccountId } = useParams();
  const { api } = useAuth();
  const { isDarkMode } = useTheme();

  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [directionFilter, setDirectionFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState('7d');
  const [selectedCall, setSelectedCall] = useState(null);

  const cardBg = isDarkMode ? 'bg-[#0c0d12]/90' : 'bg-white/90';
  const cardBorder = isDarkMode ? 'border-white/[0.08]' : 'border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const bgMain = isDarkMode ? 'bg-[#08090d]' : 'bg-gray-50';
  const inputBg = isDarkMode ? 'bg-white/5' : 'bg-gray-50';

  const fetchCalls = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/api/sub-accounts/${subAccountId}/calls`, {
        params: { date_range: dateRange, limit: 100 }
      });
      setCalls(response.data || []);
    } catch (error) {
      console.error('Failed to fetch calls:', error);
      setCalls([]);
    } finally {
      setLoading(false);
    }
  }, [subAccountId, dateRange, api]);

  useEffect(() => { fetchCalls(); }, [fetchCalls]);

  const openCallDetails = async (call) => {
    setSelectedCall(call);
    try {
      const response = await api.get(`/api/sub-accounts/${subAccountId}/calls/${call.call_id}`);
      setSelectedCall({ ...call, ...response.data });
    } catch {
      // Use what we have
    }
  };

  const fmtDuration = (s) => {
    if (!s) return '0:00';
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const fmtDate = (d) => {
    const date = new Date(d);
    const now = new Date();
    const diff = now - date;
    const mins = Math.floor(diff / 60000);
    const hrs = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 60) return `${mins}m ago`;
    if (hrs < 24) return `${hrs}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const filtered = calls.filter(call => {
    const matchSearch = !searchTerm ||
      call.phone_number?.includes(searchTerm) ||
      call.contact_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      call.agent_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchDir = directionFilter === 'all' || call.direction === directionFilter;
    const matchStatus = statusFilter === 'all' || call.status === statusFilter;
    return matchSearch && matchDir && matchStatus;
  });

  const stats = {
    total: calls.length,
    completed: calls.filter(c => c.status === 'completed').length,
    avgDuration: calls.length ? Math.round(calls.reduce((a, c) => a + (c.duration || 0), 0) / calls.length) : 0,
    appointments: calls.filter(c => c.outcome === 'appointment_booked').length,
    inbound: calls.filter(c => c.direction === 'inbound').length,
    outbound: calls.filter(c => c.direction === 'outbound').length,
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${bgMain} flex items-center justify-center`}>
        <div className="w-12 h-12 border-4 border-teal-400/30 border-t-teal-400 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${bgMain} p-6 lg:p-8`} data-testid="call-history-page">
      {isDarkMode && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 left-1/3 w-[500px] h-[500px] bg-teal-400/5 rounded-full blur-[100px]" />
        </div>
      )}

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className={`text-2xl font-semibold ${textPrimary}`}>Call History</h1>
            <p className={textSecondary}>All inbound and outbound calls from your AI agents</p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className={`px-4 py-2.5 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} text-sm focus:outline-none`}
              data-testid="date-range-select"
            >
              <option value="24h">Last 24 hours</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
            </select>
            <button onClick={fetchCalls} className={`p-2.5 rounded-xl ${inputBg} border ${cardBorder} ${textSecondary} hover:bg-white/10`} data-testid="refresh-btn">
              <RefreshCw size={18} />
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total Calls', value: stats.total, icon: History, color: 'teal' },
            { label: 'Completed', value: stats.completed, icon: CheckCircle, color: 'green' },
            { label: 'Avg Duration', value: fmtDuration(stats.avgDuration), icon: Clock, color: 'violet' },
            { label: 'Appointments', value: stats.appointments, icon: Calendar, color: 'amber' },
          ].map(s => (
            <div key={s.label} className={`${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder} p-4`}>
              <div className="flex items-center gap-2.5 mb-2">
                <div className={`w-9 h-9 rounded-lg bg-${s.color}-400/10 flex items-center justify-center`}>
                  <s.icon className={`text-${s.color}-400`} size={18} />
                </div>
                <span className={`text-xs ${textSecondary}`}>{s.label}</span>
              </div>
              <p className={`text-xl font-bold ${textPrimary}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className={`${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder} p-4 mb-6`}>
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="flex-1 relative">
              <Search size={15} className={`absolute left-3 top-1/2 -translate-y-1/2 ${textSecondary}`} />
              <input
                type="text"
                placeholder="Search by phone, contact, or agent..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-9 pr-4 py-2.5 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} text-sm placeholder-gray-500 focus:outline-none focus:border-teal-400/50`}
                data-testid="search-input"
              />
            </div>
            <div className="flex gap-1.5">
              {['all', 'inbound', 'outbound'].map(d => (
                <button key={d} onClick={() => setDirectionFilter(d)}
                  className={`px-3.5 py-2 rounded-xl text-xs font-medium transition-all ${
                    directionFilter === d
                      ? d === 'inbound' ? 'bg-teal-400/10 text-teal-400 border border-teal-400/30'
                        : d === 'outbound' ? 'bg-violet-400/10 text-violet-400 border border-violet-400/30'
                        : 'bg-teal-400/10 text-teal-400 border border-teal-400/30'
                      : `${inputBg} ${textSecondary} border ${cardBorder} hover:bg-white/10`
                  }`}
                  data-testid={`filter-${d}`}
                >
                  {d === 'all' ? `All (${stats.total})` : d === 'inbound' ? `Inbound (${stats.inbound})` : `Outbound (${stats.outbound})`}
                </button>
              ))}
            </div>
            <div className="flex gap-1.5">
              {['all', 'completed', 'missed'].map(s => (
                <button key={s} onClick={() => setStatusFilter(s)}
                  className={`px-3.5 py-2 rounded-xl text-xs font-medium transition-all ${
                    statusFilter === s
                      ? s === 'completed' ? 'bg-green-400/10 text-green-400 border border-green-400/30'
                        : s === 'missed' ? 'bg-red-400/10 text-red-400 border border-red-400/30'
                        : 'bg-teal-400/10 text-teal-400 border border-teal-400/30'
                      : `${inputBg} ${textSecondary} border ${cardBorder} hover:bg-white/10`
                  }`}
                  data-testid={`filter-status-${s}`}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Calls List */}
        {filtered.length > 0 ? (
          <div className={`${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder} overflow-hidden`}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={`border-b ${cardBorder} ${isDarkMode ? 'bg-white/[0.03]' : 'bg-gray-50'}`}>
                    {['Direction', 'Contact', 'Agent', 'Duration', 'Status', 'Time', ''].map(h => (
                      <th key={h} className={`px-5 py-3 text-left text-[11px] font-medium ${textSecondary} uppercase tracking-wider`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((call, idx) => (
                    <tr
                      key={call.call_id || idx}
                      className={`border-b ${cardBorder} hover:bg-white/[0.03] transition-colors cursor-pointer`}
                      onClick={() => openCallDetails(call)}
                      data-testid={`call-row-${call.call_id}`}
                    >
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            call.direction === 'inbound' ? 'bg-teal-400/10' : 'bg-violet-400/10'
                          }`}>
                            {call.direction === 'inbound'
                              ? <ArrowDownLeft size={15} className="text-teal-400" />
                              : <ArrowUpRight size={15} className="text-violet-400" />}
                          </div>
                          <span className={`text-xs font-medium ${
                            call.direction === 'inbound' ? 'text-teal-400' : 'text-violet-400'
                          }`}>
                            {call.direction === 'inbound' ? 'IN' : 'OUT'}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <p className={`text-sm font-medium ${textPrimary}`}>{call.contact_name || 'Unknown'}</p>
                        <p className={`text-xs ${textSecondary}`}>{call.phone_number}</p>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-1.5">
                          <Bot size={13} className="text-teal-400" />
                          <span className={`text-sm ${textPrimary}`}>{call.agent_name || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`text-sm font-mono ${textPrimary}`}>{fmtDuration(call.duration)}</span>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <CallStatusBadge call={call} />
                          {call.outcome && <OutcomeBadge outcome={call.outcome} />}
                          <GhlTagPills tags={call.tags_applied} />
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`text-sm ${textSecondary}`}>{fmtDate(call.created_at)}</span>
                      </td>
                      <td className="px-5 py-3.5 text-right" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1.5">
                          {call.has_recording && (
                            <span className="w-6 h-6 rounded flex items-center justify-center bg-teal-400/10" title="Has recording">
                              <Volume2 size={12} className="text-teal-400" />
                            </span>
                          )}
                          {call.has_transcript && (
                            <span className="w-6 h-6 rounded flex items-center justify-center bg-violet-400/10" title="Has transcript">
                              <MessageSquare size={12} className="text-violet-400" />
                            </span>
                          )}
                          <ChevronRight size={16} className={textSecondary} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className={`${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder} p-16 text-center`}>
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-400/10 to-violet-400/10 flex items-center justify-center mx-auto mb-4">
              <History className="text-gray-600" size={32} />
            </div>
            <h3 className={`text-lg font-semibold ${textPrimary} mb-1`}>No Calls Yet</h3>
            <p className={`${textSecondary} text-sm max-w-md mx-auto`}>
              Call history will appear here once your AI agents start making and receiving calls.
            </p>
          </div>
        )}
      </div>

      {/* Detail Slide-Over */}
      <AnimatePresence>
        {selectedCall && (
          <CallDetailPanel
            call={selectedCall}
            onClose={() => setSelectedCall(null)}
            textPrimary={textPrimary}
            textSecondary={textSecondary}
            inputBg={inputBg}
            cardBorder={cardBorder}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const CallHistoryPage = () => (
  <SubAccountLayout>
    <CallHistoryContent />
  </SubAccountLayout>
);

export default CallHistoryPage;
